import { useState, useEffect, useCallback, useRef } from 'react';
import { throttle } from '@/lib/core/performance';
import type { UnifiedActionsPanelMode } from '@/components/calendar-core/types';

interface ActionsPanelState {
  isOpen: boolean; // When true: panel is visible AND editing mode is active
  mode: UnifiedActionsPanelMode;
}

const STORAGE_KEY_PREFIX = 'actions_panel_';

const getDefaults = (): ActionsPanelState => ({
  isOpen: false, // Default to closed (panel collapsed + editing mode off)
  mode: 'employees', // Default to employees mode
});

export const useActionsPanel = (businessId?: string | null) => {
  // Create a unique storage key for each business
  const storageKey = businessId ? `${STORAGE_KEY_PREFIX}${businessId}` : STORAGE_KEY_PREFIX + 'default';

  // Track previous storage key to detect actual changes
  const prevStorageKeyRef = useRef<string>(storageKey);

  // Initialize state from localStorage or defaults
  const [state, setState] = useState<ActionsPanelState>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Fill in any missing properties with defaults
        return { ...getDefaults(), ...parsed };
      }
    } catch (error) {
      console.error('Error loading actions panel state from localStorage:', error);
    }

    return getDefaults();
  });

  // Re-sync state ONLY when storageKey actually changes (not on initial mount)
  useEffect(() => {
    // Skip if this is the initial mount (keys are the same)
    if (prevStorageKeyRef.current === storageKey) {
      return;
    }

    // Update ref to track the new key
    prevStorageKeyRef.current = storageKey;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setState({ ...getDefaults(), ...parsed });
      } else {
        // Reset to defaults if no stored state for new key
        setState(getDefaults());
      }
    } catch (error) {
      console.error('Error loading actions panel state from localStorage:', error);
    }
  }, [storageKey]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving actions panel state to localStorage:', error);
    }
  }, [state, storageKey]);

  // Combined toggle function (debounced to prevent rapid clicks)
  const toggle = useCallback(
    throttle(() => {
      setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
    }, 150), // 150ms debounce to prevent rapid clicks
    [state.isOpen, state.mode, businessId]
  );

  // Set mode function
  const setMode = useCallback((mode: UnifiedActionsPanelMode) => {
    setState(prev => ({ ...prev, mode }));
  }, []);

  // Reset to defaults
  const reset = useCallback(() => {
    setState({
      isOpen: false,
      mode: 'employees'
    });
  }, []);

  // For backward compatibility, expose the individual states
  const isActionsPanelCollapsed = !state.isOpen;
  // Edit mode = panel is open in any editing mode (day-actions OR shift-templates-editing)
  // 'employees' mode is also editing (drag-drop assignment), so effectively: panel open = edit mode
  const isEditingMode = state.isOpen;

  return {
    // New combined API
    isOpen: state.isOpen,
    mode: state.mode,
    toggle,
    setMode,
    reset,
    
    // Backward compatibility for existing components
    isActionsPanelCollapsed,
    isEditingMode,
    onToggleActionsPanel: toggle,
    actionsPanelMode: state.mode,
    onModeChange: setMode,
  };
};